export const PRO_PORTAL_URL = import.meta.env.VITE_PRO_PORTAL_URL;

export const PRO_PORTAL_REDIRECT_FEATURE_FLAG = "redirect-to-pro-portal";

const AUTH0_CACHE_KEY_PREFIX = "@@auth0spajs@@";

// Drops this origin's cached tokens without ending the Auth0 SSO session, so Pro Portal can still sign in silently.
const clearLocalAuthSession = (): void => {
  try {
    const cacheKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUTH0_CACHE_KEY_PREFIX)) cacheKeys.push(key);
    }
    cacheKeys.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
    sessionStorage.removeItem("authToken");
  } catch {
    // Storage may be unavailable; navigation must still proceed.
  }

  // Without this hint cookie the SDK skips its prompt=none iframe check on the next load, which otherwise stalls until it times out.
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (/^(_legacy_)?auth0\.(.+\.)?is\.authenticated$/.test(name)) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  });
};

// The redirect helper no longer requires the API surface used by the
// previous AOB session flow. Callers may provide an optional
// `onBeforeNavigate` callback to persist state before navigation.

/**
 * Hands the session over to Pro Portal, navigating in the same tab.
 * The previous implementation attempted to create an AOB session on the
 * Pro Portal side. That call frequently failed and is not required for the
 * redirect to succeed. This helper now invokes the optional
 * `onBeforeNavigate` callback (so callers can persist preferences) and
 * then navigates to the Pro Portal URL.
 * Returns false when the destination is not configured so callers can
 * surface an error.
 */
export const redirectToProPortal = async (
  // Optional callback invoked before navigation. Useful for persisting
  // preferences on the server so the write can complete before the
  // browser navigates away.
  onBeforeNavigate?: () => Promise<void>,
): Promise<boolean> => {
  if (!PRO_PORTAL_URL) {
    console.error("VITE_PRO_PORTAL_URL is not configured.");
    return false;
  }

  if (onBeforeNavigate) {
    try {
      await onBeforeNavigate();
    } catch (cbErr) {
      console.error("Error in onBeforeNavigate callback", cbErr);
      // Continue to navigate even if the callback fails.
    }
  }

  clearLocalAuthSession();
  window.location.assign(PRO_PORTAL_URL);
  return true;
};
